import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, TextField, Button, Stack,
  IconButton, InputAdornment, Checkbox, FormControlLabel, Alert,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack, Visibility, VisibilityOff, EmailOutlined, LockOutlined,
  CheckCircle, ShieldOutlined, Cloud, PersonAddOutlined,
  LoginOutlined, PhoneOutlined,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { C } from './vps/theme';
import { addUser, login as loginApi, resolveAuthUser } from '../services/userService';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    rememberMe: true,
  });

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }
    if (isSignUp && !formData.fullName.trim()) {
      setErrorMsg('Please enter your full name.');
      return;
    }
    if (isSignUp && !formData.phoneNumber.trim()) {
      setErrorMsg('Please enter your phone number.');
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        // Create the user record with the password included directly
        await addUser({
          email: formData.email.trim(),
          name: formData.fullName.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          password: formData.password,
          roles: ['Customer'],
        });
      }

      // Authenticate (covers both sign-in, and sign-up immediately after account creation)
      const result = await loginApi({
        email: formData.email.trim(),
        password: formData.password,
      });

      const authed = resolveAuthUser(result);

      if (!authed.id) {
        // eslint-disable-next-line no-console
        console.warn(
          'Login succeeded but no user id was found in the response. Raw response:',
          result?.__raw ?? result
        );
      }

      login({
        id: authed.id,
        email: authed.email ?? formData.email,
        name: authed.name ?? (isSignUp ? formData.fullName : (formData.email.split('@')[0] || 'User')),
        phoneNumber: authed.phoneNumber ?? formData.phoneNumber,
        avatar: (authed.name ?? formData.fullName ?? formData.email).slice(0, 1).toUpperCase(),
      }, authed.token);

      setSuccessMsg(isSignUp ? 'Account created successfully! Redirecting...' : 'Signed in successfully! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 800);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '90vh', background: '#F8FAFC', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Back Link */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: C.textSecondary, fontWeight: 600, textTransform: 'none', '&:hover': { color: C.primary, background: C.primaryLight } }}
          >
            Back to Home
          </Button>
        </Box>

        {/* MUI v6 Grid size */}
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">

          {/* Left Column: Hosting Services Showcase */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ pr: { md: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Cloud sx={{ color: '#FFFFFF', fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: C.textPrimary }}>
                  Hosting<Box component="span" sx={{ color: C.primary }}>Services</Box>
                </Typography>
              </Stack>

              <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 800, color: C.textPrimary, mb: 2, lineHeight: 1.2 }}>
                {isSignUp ? 'Deploy your high-performance VPS today' : 'Manage your VPS servers & cloud deployments'}
              </Typography>

              <Typography sx={{ color: C.textSecondary, fontSize: '1.02rem', lineHeight: 1.65, mb: 4 }}>
                Log in to access your root SSH credentials, server metrics, automated backups, and Cashfree billing details.
              </Typography>

              <Stack spacing={2} sx={{ mb: 4 }}>
                {[
                  'Full Root SSH & API access across all VPS plans',
                  'Claude Code & AI Agent runtimes pre-configured',
                  'Instant provisioning & 99.99% Uptime Guarantee',
                  '256-Bit SSL Secured Billing via Cashfree.com',
                ].map((feat, idx) => (
                  <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircle sx={{ color: C.success, fontSize: 20 }} />
                    <Typography sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.92rem' }}>
                      {feat}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Box sx={{ p: 2.5, borderRadius: '12px', background: C.primaryLight, border: `1px solid ${C.primary}25` }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ShieldOutlined sx={{ color: C.primary, fontSize: 22 }} />
                  <Typography variant="caption" sx={{ color: C.textPrimary, fontWeight: 700 }}>
                    Enterprise Security · Cashfree PCI DSS Level 1 Certified Checkout
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Grid>

          {/* Right Column: Login / Signup Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5 }}>
                  {isSignUp ? 'Create your Account' : 'Welcome Back'}
                </Typography>
                <Typography variant="body2" sx={{ color: C.textMuted }}>
                  {isSignUp ? 'Fill in details to get started' : 'Enter your credentials to access your control panel'}
                </Typography>
              </Box>

              {errorMsg && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{errorMsg}</Alert>}
              {successMsg && <Alert severity="success" sx={{ mb: 2.5, borderRadius: '10px' }}>{successMsg}</Alert>}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {isSignUp && (
                    <TextField fullWidth label="Full Name *" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="John Doe" />
                  )}
                  {isSignUp && (
                    <TextField fullWidth label="Phone Number *" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required placeholder="9876543210" InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                  )}
                  <TextField fullWidth label="Email Address *" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com" InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                  <TextField fullWidth label="Password *" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} required InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />

                  {!isSignUp && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <FormControlLabel control={<Checkbox name="rememberMe" checked={formData.rememberMe} onChange={handleChange} color="primary" />} label={<Typography variant="body2" sx={{ color: C.textSecondary, fontWeight: 500 }}>Remember me</Typography>} />
                      <MuiLink component="button" type="button" variant="body2" onClick={() => alert('Password reset link sent to your email.')} sx={{ color: C.primary, fontWeight: 600, textDecoration: 'none' }}>
                        Forgot password?
                      </MuiLink>
                    </Stack>
                  )}

                  <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} startIcon={isSignUp ? <PersonAddOutlined /> : <LoginOutlined />} sx={{ py: 1.5, background: C.primary, color: '#FFFFFF', fontWeight: 700, fontSize: '1rem', borderRadius: '10px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover } }}>
                    {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
                  </Button>
                </Stack>
              </form>

              <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: C.textSecondary }}>
                  {isSignUp ? 'Already have an account?' : "Don't have an account yet?"}{' '}
                  <MuiLink component="button" type="button" onClick={() => { setIsSignUp(!isSignUp); setErrorMsg(''); setSuccessMsg(''); }} sx={{ color: C.primary, fontWeight: 700, textDecoration: 'none' }}>
                    {isSignUp ? 'Sign In' : 'Create Account'}
                  </MuiLink>
                </Typography>
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;
